// src/utils/shareCache.js
import { uploadDecoupledAlbum, encodeAlbumPayload } from './share-v2';


// --- Stable stringify for consistent hashing ---
function stableStringify(obj) {
  const seen = new WeakSet();
  return JSON.stringify(obj, function (k, v) {
    if (v && typeof v === 'object') {
      if (seen.has(v)) return;
      seen.add(v);
      const keys = Object.keys(v).sort();
      const out = {};
      for (const key of keys) out[key] = v[key];
      return out;
    }
    return v;
  });
}

// --- SHA-256 (hex) ---
async function sha256Hex(str) {
  const enc = new TextEncoder();
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(str));
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2,'0')).join('');
}

// Public: compute fingerprint for an album payload
export async function fingerprintAlbum(payload) {
  return sha256Hex(stableStringify(payload));
}

const LS_PREFIX = 'shareCache:v1:';          // localStorage key prefix
const inFlight = new Map();                  // fp -> Promise<{hash}>

// Try read cached hash
export function getCachedHashSync(fp) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + fp);
    if (!raw) return null;
    const { hash, ts } = JSON.parse(raw);
    if (!hash) return null;
    // (Optional) expire after 30 days:
    if (Date.now() - (ts || 0) > 30 * 24 * 3600 * 1000) return null;
    return hash;
  } catch { return null; }
}

function setCachedHash(fp, hash) {
  try {
    localStorage.setItem(LS_PREFIX + fp, JSON.stringify({ hash, ts: Date.now() }));
  } catch {}
}

// Kick off an upload in the background; return the in-flight Promise
export function prewarmUpload(payload, mode = 'story') {
  return getOrCreateUpload(payload, mode);
}

// Returns cached result if present; otherwise starts (or reuses) a single in-flight upload.
export async function getOrCreateUpload(payload, mode = 'story') {
  const fp = await fingerprintAlbum(payload);

  // If we already have a stored hash, return it instantly
  const cached = getCachedHashSync(fp);
  if (cached) return { hash: cached, fromCache: true, fingerprint: fp };

  // De-dupe concurrent calls
  if (inFlight.has(fp)) return inFlight.get(fp);

  // IMPORTANT:
  // For *small* payloads, you may want to skip Firebase and share inline:
  // const inline = encodeAlbumPayload(payload); // uses LZ-String
  // if (inline && inline.length < 2000) return { hash: inline, inline: true, fingerprint: fp };
  // (Above is optional; your share.js already has MAX_INLINE_HASH_CHARS logic.)

  const p = (async () => {
    // Upload using the new decoupled architecture from share-v2.js
    const { hash } = await uploadDecoupledAlbum(payload, mode);
    setCachedHash(fp, hash);
    return { hash, fromCache: false, fingerprint: fp };
  })();

  inFlight.set(fp, p);
  try {
    const res = await p;
    return res;
  } finally {
    inFlight.delete(fp);
  }
}